import { ApiTranslation } from '../api-translation';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import useLocale from '@/client/hooks/use-locale';
import { cn } from '@/client/utils';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { ReactNode, useEffect, useRef, useState } from 'react';

type TextTooltipProps = React.ComponentPropsWithoutRef<typeof TooltipTrigger> & {
  side?: 'right' | 'top' | 'bottom' | 'left' | undefined;
  tooltip?: ReactNode;
  show?: boolean;
};
export const TextTooltip = ({ children, side = 'top', show, tooltip, className, ...attrs }: TextTooltipProps) => {
  const { locale, defaultLocale } = useLocale();
  show = show !== undefined ? show : locale !== defaultLocale;
  const ref = useRef<HTMLButtonElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const checkClamp = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight || '1.5');
      const maxLines = parseInt(getComputedStyle(el).webkitLineClamp || '1');
      const maxHeight = lineHeight * maxLines;
      setIsClamped(el.scrollHeight >= maxHeight + lineHeight);
    };
    checkClamp();
  }, [show, tooltip]);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    // Without asChild, Radix renders its own <button> trigger: interactive
    // children then nest inside a shrink-to-fit box (invalid HTML + w-full
    // collapses to content width — the 798e9d8 pending-button bug).
    const el = ref.current;
    if (el?.tagName === 'BUTTON' && el.querySelector('button, a, input, select, textarea, [role="button"]')) {
      console.warn(
        '[TextTooltip] interactive child inside the default trigger <button> — pass `asChild` so the wrapper element becomes the trigger.'
      );
    }
  }, []);
  return (
    <Tooltip defaultOpen={false}>
      <TooltipTrigger {...attrs} ref={ref} className={cn('text-start', className)}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        hidden={!isClamped && !show}
        side={side}
        align="center"
        className="bg-[#5D6166] text-white text-xs font-medium leading-5 p-2.5 !animate-none text-center max-w-fit break-words"
      >
        <div>{tooltip}</div>
        {isClamped && locale !== defaultLocale && (
          <div>
            <ApiTranslation>{tooltip}</ApiTranslation>
          </div>
        )}
        <TooltipArrow className="!animate-none fill-[#5D6166]" />
      </TooltipContent>
    </Tooltip>
  );
};
